import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  varchar,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ── Template tables (admin-managed, shared across all clients) ──────────────
// Edits to templates only affect NEW instances — existing client_onboarding_*
// rows are snapshot at instantiation time so in-flight onboardings never
// shift under brokers' feet.

export const onboardingTrackSchema = z.enum(["fresh", "migration"]);
export type OnboardingTrack = z.infer<typeof onboardingTrackSchema>;

export const onboardingPhases = pgTable(
  "onboarding_phases",
  {
    id: serial("id").primaryKey(),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    track: varchar("track", { length: 20 }).notNull(), // 'fresh' | 'migration'
    sortOrder: integer("sort_order").notNull().default(0),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    trackIdx: index("idx_onboarding_phases_track").on(t.track),
    trackSortIdx: index("idx_onboarding_phases_track_sort").on(t.track, t.sortOrder),
  })
);

export const insertOnboardingPhaseSchema = createInsertSchema(onboardingPhases)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(255),
    track: onboardingTrackSchema,
  });

export type OnboardingPhase = typeof onboardingPhases.$inferSelect;
export type InsertOnboardingPhase = z.infer<typeof insertOnboardingPhaseSchema>;

// Step types: 'manual' completes on click; the rest auto-complete when the
// corresponding domain event fires for the client (hooks are deferred — the
// dispatcher just needs to exist for those triggers to work).
export const onboardingCompletionTriggerSchema = z.enum([
  "manual",
  "employees_imported",
  "plans_configured",
  "payroll_connected",
  "census_uploaded",
  "enrollment_confirmed",
  "agency_created",
  "team_added",
]);
export type OnboardingCompletionTrigger = z.infer<typeof onboardingCompletionTriggerSchema>;

export const onboardingResponsiblePartySchema = z.enum(["broker", "employer", "administr"]);
export type OnboardingResponsibleParty = z.infer<typeof onboardingResponsiblePartySchema>;

export const onboardingStepTemplates = pgTable(
  "onboarding_step_templates",
  {
    id: serial("id").primaryKey(),
    phaseId: integer("phase_id").notNull(), // FK → onboarding_phases.id (cascade handled in delete logic)
    name: varchar("name", { length: 500 }).notNull(),
    description: text("description"),
    responsibleParty: varchar("responsible_party", { length: 50 }).notNull().default("broker"),
    completionTrigger: varchar("completion_trigger", { length: 100 }).notNull().default("manual"),
    slaDays: integer("sla_days"), // days from instance start; null = no SLA
    sortOrder: integer("sort_order").notNull().default(0),
    isRequired: boolean("is_required").notNull().default(true),
    isActive: boolean("is_active").notNull().default(true),
    notes: text("notes"), // admin-only internal notes
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    phaseIdx: index("idx_onboarding_step_templates_phase").on(t.phaseId),
    phaseSortIdx: index("idx_onboarding_step_templates_phase_sort").on(t.phaseId, t.sortOrder),
  })
);

export const insertOnboardingStepTemplateSchema = createInsertSchema(onboardingStepTemplates)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(500),
    responsibleParty: onboardingResponsiblePartySchema.default("broker"),
    completionTrigger: onboardingCompletionTriggerSchema.default("manual"),
    slaDays: z.number().int().min(0).optional().nullable(),
  });

export type OnboardingStepTemplate = typeof onboardingStepTemplates.$inferSelect;
export type InsertOnboardingStepTemplate = z.infer<typeof insertOnboardingStepTemplateSchema>;

// ── Per-client instance tables ──────────────────────────────────────────────

export const onboardingInstanceStatusSchema = z.enum(["active", "complete", "paused"]);
export const onboardingStepStatusSchema = z.enum(["pending", "in_progress", "complete", "skipped"]);

export const clientOnboardingInstances = pgTable(
  "client_onboarding_instances",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(), // FK → clients.id (loose ref)
    agencyId: varchar("agency_id", { length: 255 }),
    track: varchar("track", { length: 20 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("active"),
    startDate: timestamp("start_date").defaultNow().notNull(),
    assignedAdminUserId: varchar("assigned_admin_user_id", { length: 255 }),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    clientIdx: index("idx_client_onboarding_instances_client").on(t.clientId),
    agencyIdx: index("idx_client_onboarding_instances_agency").on(t.agencyId),
    statusIdx: index("idx_client_onboarding_instances_status").on(t.status),
  })
);

export const clientOnboardingSteps = pgTable(
  "client_onboarding_steps",
  {
    id: serial("id").primaryKey(),
    instanceId: integer("instance_id").notNull(),
    templateStepId: integer("template_step_id").notNull(), // traceability only
    // Snapshot of template values at instantiation time
    name: varchar("name", { length: 500 }).notNull(),
    description: text("description"),
    responsibleParty: varchar("responsible_party", { length: 50 }).notNull(),
    completionTrigger: varchar("completion_trigger", { length: 100 }).notNull(),
    phaseId: integer("phase_id").notNull(),
    phaseName: varchar("phase_name", { length: 255 }).notNull(),
    phaseOrder: integer("phase_order").notNull(),
    stepOrder: integer("step_order").notNull(),
    slaDays: integer("sla_days"),
    dueDate: timestamp("due_date"),
    isRequired: boolean("is_required").notNull().default(true),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    completedAt: timestamp("completed_at"),
    completedBy: varchar("completed_by", { length: 255 }), // user id or 'system'
    notes: text("notes"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    instanceIdx: index("idx_client_onboarding_steps_instance").on(t.instanceId),
    instanceStatusIdx: index("idx_client_onboarding_steps_instance_status").on(t.instanceId, t.status),
  })
);

export type ClientOnboardingInstance = typeof clientOnboardingInstances.$inferSelect;
export type ClientOnboardingStep = typeof clientOnboardingSteps.$inferSelect;
